import { prefersReducedMotion, debounce, preloadImage, isClickedOutside } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const ZOOM_DIALOG_SELECTOR = 'dialog-component[data-zoom-dialog]';
const zoomDialogControllers = new WeakMap();
/**
 * A controller that adds zoom dialog behavior to dialog-component elements.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @property {Refs} refs - The dialog refs from the host component.
 */
/**
 * @typedef {HTMLElement & {
 *   refs: Refs,
 *   open?: (index: number, event?: PointerEvent) => void,
 *   close?: () => void,
 *   handleKeyDown?: (event: KeyboardEvent) => void,
 *   handleThumbnailClick?: (index: number) => void,
 *   handleThumbnailPointerEnter?: (index: number) => void,
 *   selectThumbnail?: (index: number, options?: { behavior: ScrollBehavior }) => void,
 * }} ZoomDialogHost
 */
export class ZoomDialog {
  /**
   * @param {ZoomDialogHost} host
   */
  /**
   * Closes the dialog when the user clicks outside of it (e.g. on the backdrop).
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const dialog = this.dialog;
    if (!dialog?.open) return;
    if (isClickedOutside(event, dialog)) {
      this.close();
    }
  };

  constructor(host) {
    this.host = host;
    this.#highResImagesLoaded = /** @type {Set<string>} */ (new Set());
    this.handleScroll = debounce(this.handleScroll.bind(this), 50);

    const scroller = this.scroller;
    if (scroller) scroller.addEventListener('scroll', this.handleScroll);
  }

  /** @type {ZoomDialogHost} */
  host;

  /** @type {Set<string>} */
  #highResImagesLoaded;

  get refs() {
    return /** @type {Refs} */ (this.host.refs);
  }

  get dialog() {
    return this.refs.dialog ?? this.host.querySelector('dialog');
  }

  get thumbnails() {
    return this.host.querySelector('[data-zoom-thumbnails]') ?? this.refs.thumbnails;
  }

  get media() {
    return Array.from(this.host.querySelectorAll('[data-zoom-media]')).filter(
      (element) => element instanceof HTMLElement
    );
  }

  get scroller() {
    return this.host.querySelector('slideshow-slides') ?? this.dialog;
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    if (event?.preventDefault) event.preventDefault();

    const dialog = this.dialog;
    const thumbnails = this.thumbnails;
    const media = this.media;
    const targetImage = media[index];
    const targetThumbnail = thumbnails?.children[index];

    if (!dialog || !targetImage) return;

    const open = () => {
      document.body.setAttribute('data-zoom-dialog-open', '');
      dialog.showModal();
      this.host.addEventListener('click', this.#handleClick);

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event?.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;
    const sourceElement = sourceImage instanceof HTMLElement ? sourceImage : null;

    open();
    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   */
  handleScroll() {
    const media = this.media;
    const thumbnails = this.thumbnails;
    if (!media.length || !thumbnails) return;

    getMostVisibleElement(media).then((mostVisibleElement) => {
      const activeIndex = media.indexOf(mostVisibleElement);
      const targetThumbnail = thumbnails.children[activeIndex];

      if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

      Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === activeIndex}`);
      });

      this.#loadHighResolutionImage(mostVisibleElement);
      this.host.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
    });
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const dialog = this.dialog;
    if (!dialog) return;

    // Sync PDP carousel to the slide the shopper was viewing before teardown.
    await this.#syncSelectedMediaToGallery();

    if (prefersReducedMotion()) return this.closeDialog();

    if (!dialog.classList.contains('dialog--closing')) {
      dialog.classList.add('dialog--closing');
      await waitForTransition(dialog);
      dialog.classList.remove('dialog--closing');
    }

    this.closeDialog();
  }

  /**
   * Emit the visible zoom slide index so media-gallery can mirror it on the PDP.
   */
  async #syncSelectedMediaToGallery() {
    const media = this.media;
    if (!media.length) return;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    if (activeIndex < 0) return;

    this.host.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }

  closeDialog() {
    const dialog = this.dialog;
    if (!dialog) return;
    this.host.removeEventListener('click', this.#handleClick);
    dialog.close();
    document.body.removeAttribute('data-zoom-dialog-open');
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const media = this.media;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const thumbnails = this.thumbnails;
    const media = this.media;
    if (!thumbnails || !thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= thumbnails.children.length) return;

    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.#loadHighResolutionImage(targetImage);
    }
    this.host.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

/**
 * @param {import('./dialog').DialogComponent} host
 */
/**
 * @param {ZoomDialogHost} host
 */
function enhanceZoomDialog(host) {
  if (zoomDialogControllers.has(host)) return;

  const controller = new ZoomDialog(host);
  zoomDialogControllers.set(host, controller);

  host.open = controller.open.bind(controller);
  host.close = controller.close.bind(controller);
  host.handleKeyDown = controller.handleKeyDown.bind(controller);
  host.handleThumbnailClick = controller.handleThumbnailClick.bind(controller);
  host.handleThumbnailPointerEnter = controller.handleThumbnailPointerEnter.bind(controller);
  host.selectThumbnail = controller.selectThumbnail.bind(controller);
}

function initZoomDialogs(root = document) {
  root.querySelectorAll(ZOOM_DIALOG_SELECTOR).forEach((element) => {
    if (!(element instanceof HTMLElement)) return;
    enhanceZoomDialog(/** @type {ZoomDialogHost} */ (element));
  });
}

/**
 * @param {Node} node
 */
function handleAddedNode(node) {
  if (!(node instanceof Element)) return;
  if (node.matches(ZOOM_DIALOG_SELECTOR)) enhanceZoomDialog(/** @type {ZoomDialogHost} */ (node));
  node.querySelectorAll?.(ZOOM_DIALOG_SELECTOR).forEach((element) => {
    if (!(element instanceof HTMLElement)) return;
    enhanceZoomDialog(/** @type {ZoomDialogHost} */ (element));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initZoomDialogs());
} else {
  initZoomDialogs();
}

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach(handleAddedNode);
  }
});

observer.observe(document.documentElement, { childList: true, subtree: true });

/**
 * @param {HTMLElement} element
 * @param {string} propertyName
 * @returns {Promise<void>}
 */
function waitForTransition(element, propertyName = 'transform') {
  return new Promise((resolve) => {
    let resolved = false;

    const finish = () => {
      if (resolved) return;
      resolved = true;
      element.removeEventListener('transitionend', onEnd);
      resolve();
    };

    const onEnd = (event) => {
      if (event.target !== element) return;
      if (propertyName && event.propertyName !== propertyName) return;
      finish();
    };

    element.addEventListener('transitionend', onEnd);

    requestAnimationFrame(() => {
      if (!resolved) {
        setTimeout(finish, 400);
      }
    });
  });
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
